<script setup lang="ts">
import { ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { errorMessage } from '@/utils/format';

const props = defineProps<{
  entityType: string;
  entityId: string | number | null | undefined;
  /** 折叠标题右侧的说明，不同模块措辞不同 */
  hint?: string;
}>();

interface Change { field: string; label: string; before: string | null; after: string | null }
interface Entry {
  id: number;
  actionLabel: string;
  actorName: string | null;
  createdAt: string;
  changes: Change[];
  action: string;
}

const open = ref(false);
const loading = ref(false);
const error = ref('');
const entries = ref<Entry[]>([]);
const loadedFor = ref<string>('');

function stamp(value: string): string {
  return value.replace('T', ' ').slice(0, 16);
}

/**
 * 展开时才去取。
 *
 * 每打开一条记录就顺手拉一次历史，对一屏几十条的列表就是几十个请求，
 * 而大多数时候没人要看历史——按需加载才对得起这个功能的使用频率。
 */
async function load(): Promise<void> {
  const key = `${props.entityType}:${props.entityId}`;
  if (!props.entityId || loadedFor.value === key) return;
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.recordHistory(props.entityType, props.entityId);
    entries.value = (payload.entries ?? []) as unknown as Entry[];
    loadedFor.value = key;
  } catch (caught) {
    entries.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

function toggle(): void {
  open.value = !open.value;
  if (open.value) void load();
}

// 切到另一条记录时，已加载的历史要作废，否则会把上一条的历史当成这条的
watch(() => `${props.entityType}:${props.entityId}`, () => {
  entries.value = [];
  loadedFor.value = '';
  if (open.value) void load();
});
</script>

<template>
  <section v-if="entityId" class="record-history">
    <header>
      <button type="button" :aria-expanded="open" @click="toggle">
        {{ open ? '收起变更历史' : '查看变更历史' }}
      </button>
      <small>{{ hint || '谁在什么时候把哪个字段改成了什么。' }}</small>
    </header>

    <template v-if="open">
      <p v-if="loading" class="rh-state">正在读取…</p>
      <p v-else-if="error" class="rh-state rh-error">{{ error }}</p>
      <p v-else-if="!entries.length" class="rh-state">这条记录还没有留下改动记录。</p>

      <ol v-else class="rh-list">
        <li v-for="entry in entries" :key="entry.id">
          <div class="rh-head">
            <b>{{ entry.actionLabel }}</b>
            <span>{{ entry.actorName || '未知操作人' }}</span>
            <time>{{ stamp(entry.createdAt) }}</time>
          </div>
          <ul v-if="entry.changes.length" class="rh-changes">
            <li v-for="change in entry.changes" :key="change.field">
              <span class="rh-field">{{ change.label }}</span>
              <s>{{ change.before ?? '（空）' }}</s>
              <i aria-hidden="true">→</i>
              <b>{{ change.after ?? '（空）' }}</b>
            </li>
          </ul>
          <small v-else class="rh-nochange">
            {{ entry.action === 'create' ? '新建了这条记录' : '本次没有字段级差异' }}
          </small>
        </li>
      </ol>
    </template>
  </section>
</template>

<style scoped>
.record-history { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--line); }
.record-history > header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.record-history > header button {
  padding: 4px 11px;
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--blue);
  font: 700 12px var(--body);
  cursor: pointer;
}

.record-history > header button:hover { border-color: var(--blue); }
.record-history > header small { color: var(--muted); font-size: 12px; }
.rh-state { margin: 8px 0 0; color: var(--muted); font-size: 12px; }
.rh-error { color: var(--risk); }

.rh-list {
  display: grid;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}

.rh-list > li { padding: 8px 10px; border-left: 2px solid var(--blue); background: var(--paper); }
.rh-head { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; font-size: 12px; }
.rh-head b { color: var(--blue); }
.rh-head span { color: var(--ink); }
.rh-head time { margin-left: auto; color: var(--muted); }
.rh-changes { display: grid; gap: 3px; margin: 6px 0 0; padding: 0; list-style: none; font-size: 12px; }
.rh-changes > li { display: flex; gap: 6px; align-items: baseline; flex-wrap: wrap; }
.rh-field { min-width: 64px; color: var(--muted); }
.rh-changes s { color: var(--muted); }
.rh-changes i { color: var(--muted); font-style: normal; }
.rh-changes b { color: var(--ink); }
.rh-nochange { display: block; margin-top: 4px; color: var(--muted); font-size: 12px; }
</style>
